import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, ArrowBack as BackIcon } from "@mui/icons-material";
import {
  GameweekList,
  CreateGameweekModal,
  EditGameweekModal,
} from "../components/gameweeks";
import { get, post, del, patch } from "../utils/networkUtils";
import { Link as RouterLink } from "react-router-dom";

const TournamentGameweeksPage = () => {
  const { id: tournamentId } = useParams();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState(null);
  const [gameweeks, setGameweeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    gameweekId: null,
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedGameweek, setSelectedGameweek] = useState(null);

  const isWorldCup =
    tournament?.competition_name?.toLowerCase().includes("world cup") ||
    tournament?.competition_name?.toLowerCase().includes("fifa world");

  const fetchTournament = async () => {
    try {
      const response = await get(`/admin/tournament-calendar/${tournamentId}`);
      if (response?.data) {
        setTournament(response.data);
      } else {
        setTournament(null);
      }
    } catch (error) {
      console.error("Failed to fetch tournament:", error);
      setTournament(null);
    }
  };

  const fetchGameweeks = async () => {
    try {
      setLoading(true);
      const response = await get(
        `/admin/gameweek?tournamentCalendarId=${tournamentId}&limit=100`
      );

      if (response?.data?.gameweeks) {
        const mappedGameweeks = response.data.gameweeks.map((gw) => ({
          id: gw.id,
          tournamentId: gw.tournamentCalendarId,
          number: gw.number,
          name: gw.name,
          assignedMatches: gw.assignedMatches || 0,
          status: gw.status,
          startsAt: gw.startsAt,
          endsAt: gw.endsAt,
          deadlineAt: gw.deadlineAt,
          budgetInitial: gw.budgetInitial,
          isDoubleGameweek: gw.isDoubleGameweek,
          knockoutRound: gw.knockoutRound,
          createdAt: gw.createdAt,
          updatedAt: gw.updatedAt,
        }));
        setGameweeks(mappedGameweeks);
      }
    } catch (error) {
      console.error("Failed to fetch gameweeks:", error);
      setSnackbar({
        open: true,
        message: "Failed to load gameweeks. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      await fetchTournament();
      await fetchGameweeks();
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const handleCreateGameweek = () => {
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async (formData) => {
    if (!tournamentId) {
      setSnackbar({
        open: true,
        message: "Tournament not loaded yet. Please try again.",
        severity: "error",
      });
      return;
    }

    try {
      const payload = {
        tournamentCalendarId: tournamentId,
        number: formData.number,
        name: formData.name,
        status: formData.status || "upcoming",
        budgetInitial: formData.budgetInitial,
        isDoubleGameweek: formData.isDoubleGameweek,
      };

      const response = await post("/admin/gameweek/", payload);

      if (response?.data) {
        const newGameweek = {
          id: response.data.id,
          tournamentId: response.data.tournamentCalendarId,
          number: response.data.number,
          name: response.data.name,
          assignedMatches: response.data.assignedMatches || 0,
          status: response.data.status,
          startsAt: response.data.startsAt,
          endsAt: response.data.endsAt,
          deadlineAt: response.data.deadlineAt,
          budgetInitial: response.data.budgetInitial,
          isDoubleGameweek: response.data.isDoubleGameweek,
          knockoutRound: response.data.knockoutRound,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
        };

        setGameweeks((prev) =>
          [...prev, newGameweek].sort((a, b) => a.number - b.number)
        );
        setSnackbar({
          open: true,
          message: `Gameweek "${formData.name}" created successfully`,
          severity: "success",
        });
        setCreateModalOpen(false);
      }
    } catch (error) {
      console.error("Failed to create gameweek:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to create gameweek",
        severity: "error",
      });
    }
  };

  const handleViewGameweek = (gameweekId) => {
    navigate(`/dashboard/tournaments/${tournamentId}/gameweeks/${gameweekId}`);
  };

  const handleEditGameweek = (gameweekId) => {
    const gameweek = gameweeks.find((gw) => gw.id === gameweekId);
    if (gameweek) {
      setSelectedGameweek(gameweek);
      setEditModalOpen(true);
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        status: formData.status,
        budgetInitial: formData.budgetInitial,
        isDoubleGameweek: formData.isDoubleGameweek,
        deadlineAt: formData.deadlineAt,
      };

      const response = await patch(
        `/admin/gameweek/${selectedGameweek.id}`,
        payload
      );

      if (response?.data) {
        setGameweeks((prev) =>
          prev.map((gw) =>
            gw.id === selectedGameweek.id
              ? {
                  ...gw,
                  name: response.data.name,
                  status: response.data.status,
                  budgetInitial: response.data.budgetInitial,
                  isDoubleGameweek: response.data.isDoubleGameweek,
                  startsAt: response.data.startsAt,
                  endsAt: response.data.endsAt,
                  deadlineAt: response.data.deadlineAt,
                  updatedAt: response.data.updatedAt,
                }
              : gw
          )
        );
        setSnackbar({
          open: true,
          message: `Gameweek "${formData.name}" updated successfully`,
          severity: "success",
        });
        setSelectedGameweek(null);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Failed to update gameweek:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update gameweek",
        severity: "error",
      });
      return false;
    }
  };

  const handleAssignMatches = (gameweekId) => {
    navigate(
      `/dashboard/tournaments/${tournamentId}/gameweek-builder?gameweek=${gameweekId}`
    );
  };

  const handleStatusChange = async (gameweekId, newStatus) => {
    try {
      await patch(`/admin/gameweek/${gameweekId}/status`, {
        status: newStatus,
      });

      setGameweeks((prev) =>
        prev.map((gw) =>
          gw.id === gameweekId ? { ...gw, status: newStatus } : gw
        )
      );

      const statusLabels = {
        upcoming: "Upcoming",
        open: "Open",
        locked: "Locked",
        live: "Live",
        finalised: "Finalised",
      };

      setSnackbar({
        open: true,
        message: `Gameweek status changed to ${statusLabels[newStatus]}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update status",
        severity: "error",
      });
    }
  };

  const handleDeleteClick = (gameweekId) => {
    setDeleteDialog({ open: true, gameweekId });
  };

  const handleDeleteConfirm = async () => {
    try {
      await del(`/admin/gameweek/${deleteDialog.gameweekId}`);

      setGameweeks((prev) =>
        prev.filter((gw) => gw.id !== deleteDialog.gameweekId)
      );
      setSnackbar({
        open: true,
        message: "Gameweek deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete gameweek:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to delete gameweek. It may have assigned matches.",
        severity: "error",
      });
    } finally {
      setDeleteDialog({ open: false, gameweekId: null });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const existingGameweekNumbers = gameweeks.map((gw) => gw.number);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  if (isWorldCup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Gameweek management is only available for non-World Cup tournaments.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}
          sx={{ mt: 2 }}
        >
          Back to Tournament
        </Button>
      </Container>
    );
  }

  const finalisedGameweeks = gameweeks.filter(
    (gw) => gw.status === "finalised"
  ).length;
  const openGameweeks = gameweeks.filter(
    (gw) => gw.status === "open"
  ).length;
  const lockedGameweeks = gameweeks.filter(
    (gw) => gw.status === "locked"
  ).length;
  const liveGameweeks = gameweeks.filter(
    (gw) => gw.status === "live"
  ).length;

  return (
    <>
      <Helmet>
        <title>
          {tournament.competition_name} - Gameweeks | DreamTeam Admin
        </title>
      </Helmet>

      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component={RouterLink}
            to="/dashboard/tournaments"
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${tournamentId}`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {tournament.competition_name}
          </Typography>
          <Typography color="text.primary">Gameweeks</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">{tournament.competition_name}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateGameweek}
            >
              Create Gameweek
            </Button>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage gameweeks and match assignments for this tournament
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Gameweeks
              </Typography>
              <Typography variant="h4">{gameweeks.length}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Open
              </Typography>
              <Typography variant="h4">{openGameweeks}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Locked
              </Typography>
              <Typography variant="h4">{lockedGameweeks}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Live
              </Typography>
              <Typography variant="h4">{liveGameweeks}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Finalised
              </Typography>
              <Typography variant="h4">{finalisedGameweeks}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Gameweeks
            </Typography>
            <GameweekList
              gameweeks={gameweeks}
              onView={handleViewGameweek}
              onEdit={handleEditGameweek}
              onDelete={handleDeleteClick}
              onStatusChange={handleStatusChange}
              onAssignMatches={handleAssignMatches}
              tournamentId={tournamentId}
            />
          </CardContent>
        </Card>
      </Container>

      <CreateGameweekModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        tournamentId={tournamentId}
        existingGameweekNumbers={existingGameweekNumbers}
        onSubmit={handleCreateSubmit}
      />

      <EditGameweekModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        gameweek={selectedGameweek}
        onSubmit={handleEditSubmit}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, gameweekId: null })}
      >
        <DialogTitle>Delete Gameweek?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this gameweek?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Gameweeks with assigned matches cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, gameweekId: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TournamentGameweeksPage;
